/* eslint-disable react/no-unescaped-entities */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatErrorDetail } from '../../utils/formatError';
import QUESTIONS, { ASSESSMENT_SECTIONS } from '../../config/stage1Questions';
import './Assessment.css';
import InlineFeedback, { flushPendingFeedback, WELCOME_FB, getSectionFBIds, RESULTS_FB, OVERALL_FB } from '../../components/InlineFeedback';
import { SectionEndFeedback } from '../../components/SectionFeedback';
import VoiceTextInput from '../../components/VoiceTextInput';
import { SingleSelect, MultiSelect, ScaleInput, TextInput, NameFields } from './QuestionRenderers';

const API = process.env.REACT_APP_BACKEND_URL;

const NZ_REGIONS = [
  "Northland", "Auckland", "Waikato", "Bay of Plenty", "Gisborne",
  "Hawke's Bay", "Taranaki", "Manawatu-Whanganui", "Wellington",
  "Tasman", "Nelson", "Marlborough", "West Coast", "Canterbury",
  "Otago", "Southland", "Overseas"
];

const SITUATIONS = [
  "I have been made redundant",
  "I am choosing to leave a role",
  "I am returning after time out, for example caregiving or health",
  "I am looking for work and not finding it",
  "I am between things and working it out",
  "I am already working for myself",
  "Something else",
];

const AGE_BANDS = [
  "Under 25", "25 to 34", "35 to 44", "45 to 54",
  "55 to 64", "65 plus", "Prefer not to say"
];

/* ===================================================================
   SCREEN 1: WELCOME / HOW IT WORKS (Appendix A locked copy)
   With optional pilot code entry
   =================================================================== */
function WelcomeScreen({ onStartEmail, onStartCode, loading, error }) {
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [showPilotCode, setShowPilotCode] = useState(false);
  const [pilotCode, setPilotCode] = useState('');
  const [codeErr, setCodeErr] = useState('');

  const handleBegin = async () => {
    const code = accessCode.trim().toUpperCase();
    if (!code) {
      setCodeErr('Please enter your access code.');
      return;
    }
    try {
      const res = await fetch(`${API}/api/validate-access-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!data.valid) {
        setCodeErr('That access code isn\u2019t right. Check with whoever invited you.');
        return;
      }
    } catch {
      setCodeErr('Unable to verify code. Please try again.');
      return;
    }
    setCodeErr('');
    if (showPilotCode && pilotCode.trim()) {
      onStartCode(email, pilotCode);
    } else {
      onStartEmail(email);
    }
  };

  return (
    <div className="assess-welcome-split" data-testid="assessment-welcome">
      {/* Blush sidebar */}
      <div className="assess-welcome-sidebar">
        <img src="/spring-logo-colour-tagline.webp" alt="SPRING" className="assess-sidebar-logo" />
        <div className="assess-sidebar-content">
          <span className="assess-sidebar-eyebrow">The assessment</span>
          <h2 className="assess-sidebar-headline">every ending is a new beginning. this is where yours starts.</h2>
        </div>
        <div className="assess-sidebar-footer">&copy; Spring Collective Limited 2026</div>
      </div>

      {/* White main area */}
      <div className="assess-welcome-main">
        <div className="assess-welcome-inner">
          <h1 className="assess-welcome-title">welcome to SPRING.</h1>
          <p className="assess-welcome-subtitle">Before you start, here's how this works, no surprises.</p>
          <div className="assess-welcome-body">
            <p style={{ fontSize: 17, lineHeight: 1.55, color: 'var(--ink)', marginBottom: 20 }}>The assessment is free. It takes about 20 to 30 minutes, or longer if you want to sit with it. There are no right answers, and being straight with us is what lets us be straight with you, so answer as honestly as you can, about where you actually are, not where you think you should be.</p>
            <p style={{ fontSize: 17, lineHeight: 1.55, color: 'var(--ink)', marginBottom: 20 }}>As you go, you can move back and change any answer at any point before you finish, nothing is locked in until you complete it. Take your time and get it right.</p>
            <p style={{ fontSize: 17, lineHeight: 1.55, color: 'var(--ink)', marginBottom: 20 }}>When you're done, we'll show you your results, a full report on your assessment: a plain read on where your strengths sit and a suggested direction, with the reasons behind it. It's a recommendation, not a verdict, what you do next is always your choice. And if your answers don't yet point to a clear direction, we'll tell you that honestly rather than guess.</p>
          </div>
          <div className="assess-email-capture">
            <label htmlFor="assess-email">Pop your email in below so we can send you your results and keep them safe for you.</label>
            <input
              id="assess-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              data-testid="assessment-email-input"
            />

            <label htmlFor="assess-access-code" style={{ marginTop: 16 }}>Access code</label>
            <input
              id="assess-access-code"
              type="text"
              value={accessCode}
              onChange={e => { setAccessCode(e.target.value.toUpperCase()); setCodeErr(''); }}
              placeholder="Enter your access code"
              data-testid="assessment-access-code-input"
            />

            {/* Optional pilot code */}
            <button type="button" className="assess-privacy-toggle" onClick={() => setShowPilotCode(!showPilotCode)}
              data-testid="toggle-pilot-code" style={{ marginTop: 8 }}>
              {showPilotCode ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <span>Have an invitation code?</span>
            </button>
            {showPilotCode && (
              <input
                type="text"
                value={pilotCode}
                onChange={e => setPilotCode(e.target.value.toUpperCase())}
                placeholder="SPRING-XXXXXX"
                data-testid="pilot-code-input"
                style={{ marginTop: 8 }}
              />
            )}

            {(error || codeErr) && <p className="assess-error" data-testid="welcome-error">{codeErr || error}</p>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 16 }}>
              <button
                className="assess-btn-primary"
                onClick={handleBegin}
                disabled={loading || !email.includes('@') || !accessCode.trim()}
                data-testid="assessment-begin-btn"
              >
                {loading ? 'Starting...' : "Let's begin"}
              </button>
              <span style={{ fontSize: 14, color: 'var(--stone)' }}>About 20 to 30 minutes</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ===================================================================
   SCREEN 2: DETAILS AND CONSENT
   =================================================================== */
function DetailsConsentScreen({ code, wave, email: prefillEmail, onSubmit, loading, error }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: prefillEmail || '', region: '', age_band: '', situation: '',
    consent_data: false, consent_not_advice: false, consent_contact: false, consent_conversation: false,
  });
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const canSubmit = form.first_name && form.last_name && form.email.includes('@') &&
    form.region && form.situation && form.consent_data && form.consent_not_advice;

  return (
    <div className="assess-welcome" data-testid="details-consent">
      <div className="assess-welcome-inner" style={{ maxWidth: 640 }}>
        <h1 className="assess-welcome-title">A few details first</h1>
        <p className="assess-welcome-subtitle" style={{ marginTop: -16, marginBottom: 24 }}>
          We need enough to send your results back to you and to make sense of your feedback afterwards. Nothing here is shared outside SPRING, and nothing is sold.
        </p>

        <div className="assess-detail-form">
          <div className="assess-field-group">
            <label>First name *</label>
            <input type="text" value={form.first_name} onChange={e => set('first_name', e.target.value)} data-testid="detail-first-name" />
          </div>
          <div className="assess-field-group">
            <label>Last name *</label>
            <input type="text" value={form.last_name} onChange={e => set('last_name', e.target.value)} data-testid="detail-last-name" />
          </div>
          <div className="assess-field-group">
            <label>Email *</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} data-testid="detail-email" />
          </div>
          <div className="assess-field-group">
            <label>Which of these is closest to where you are right now? *</label>
            <select value={form.region} onChange={e => set('region', e.target.value)} data-testid="detail-region">
              <option value="">Select region...</option>
              {NZ_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="assess-field-group">
            <label>Age band (optional)</label>
            <select value={form.age_band} onChange={e => set('age_band', e.target.value)} data-testid="detail-age-band">
              <option value="">Prefer not to say</option>
              {AGE_BANDS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div className="assess-field-group">
            <label>Your situation *</label>
            <div className="assess-options compact">
              {SITUATIONS.map((s, i) => (
                <button key={i} className={`assess-option ${form.situation === s ? 'selected' : ''}`}
                  onClick={() => set('situation', s)} data-testid={`situation-${i}`}>{s}</button>
              ))}
            </div>
          </div>

          <div className="assess-consent-section">
            <label className="assess-consent-item">
              <input type="checkbox" checked={form.consent_data} onChange={e => set('consent_data', e.target.checked)} data-testid="consent-data" />
              <span>I understand that my answers, my results and my feedback will be sent to the SPRING team at hello@springcollective.biz so they can read them and improve the programme. *</span>
            </label>
            <label className="assess-consent-item">
              <input type="checkbox" checked={form.consent_not_advice} onChange={e => set('consent_not_advice', e.target.checked)} data-testid="consent-advice" />
              <span>I understand SPRING is not financial, legal or employment advice, and that any pathway suggested to me is a recommendation and not a verdict. *</span>
            </label>
            <label className="assess-consent-item">
              <input type="checkbox" checked={form.consent_contact} onChange={e => set('consent_contact', e.target.checked)} data-testid="consent-contact" />
              <span>SPRING can contact me about the programme in future.</span>
            </label>
            <label className="assess-consent-item">
              <input type="checkbox" checked={form.consent_conversation} onChange={e => set('consent_conversation', e.target.checked)} data-testid="consent-convo" />
              <span>SPRING can come back to me for a short conversation about my experience.</span>
            </label>

            {/* Privacy statement toggle */}
            <button
              type="button"
              className="assess-privacy-toggle"
              onClick={() => setPrivacyOpen(!privacyOpen)}
              data-testid="privacy-toggle"
            >
              {privacyOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              <span>Read our privacy statement</span>
            </button>

            {privacyOpen && (
              <div className="assess-privacy-panel" data-testid="privacy-panel">
                <h4>How we handle your data</h4>
                <p>The Springboard Group Ltd (trading as SPRING) collects your information for the purpose of delivering this assessment and, if you choose to continue, the guided programme that follows it.</p>

                <h4>What we collect</h4>
                <p>Your name, email address, region, age band, situation, your assessment answers, your results, your feedback responses, and basic session data such as the device type you used and how long you spent on each section. If you provide a phone number for a follow-up conversation, we collect that too.</p>

                <h4>How we use it</h4>
                <ul>
                  <li>To generate your personal assessment results and pathway recommendation.</li>
                  <li>To send you your results by email.</li>
                  <li>To send a copy of your results and feedback to the SPRING team (Louise Keaney and Richard Hollingum) at hello@springcollective.biz so they can read it, learn from it, and improve the programme.</li>
                  <li>If you gave optional consent, to contact you about the programme or to arrange a short conversation about your experience.</li>
                </ul>

                <h4>Who sees it</h4>
                <p>Your data is seen by Louise Keaney and Richard Hollingum, the founders of SPRING. It is not shared with any third party, sold, or used for advertising. Assessment data is processed by a language model (Claude, provided by Anthropic) to generate your results. Anthropic does not retain or train on this data.</p>

                <h4>Where it is stored</h4>
                <p>Your data is stored securely in an encrypted database hosted on infrastructure managed by Emergent Labs. Emails are sent via Resend. Files are stored using Emergent Object Storage. All services operate under standard data protection agreements.</p>

                <h4>How long we keep it</h4>
                <p>We keep your data for the duration of the pilot programme and for up to 12 months afterwards, unless you ask us to delete it sooner. After that period, all personally identifiable data is deleted or anonymised.</p>

                <h4>Your rights</h4>
                <p>You can ask us to show you what data we hold about you, correct it, or delete it at any time. Email hello@springcollective.biz and we will action your request within five working days. Under the New Zealand Privacy Act 2020 you also have the right to complain to the Office of the Privacy Commissioner.</p>

                <h4>Invitation codes</h4>
                <p>Your invitation code is single-use and is linked to your email address once you register. It cannot be transferred or reused.</p>

                <h4>Changes to this statement</h4>
                <p>If we change how we handle your data, we will email you at the address you provided and update this statement. The current version is dated 1 March 2026.</p>

                <p className="assess-privacy-contact">Questions? Email <strong>hello@springcollective.biz</strong></p>
              </div>
            )}
          </div>

          {error && <p className="assess-error">{error}</p>}
          <button className="assess-btn-primary" onClick={() => onSubmit({ ...form, code, wave })}
            disabled={loading || !canSubmit} data-testid="details-continue-btn">
            {loading ? 'Saving...' : 'Continue'}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================================================================
   SECTION INTRODUCTION SCREENS (Part 8 locked copy)
   =================================================================== */
const SECTION_INTROS = {
  1: {
    label: "SECTION 1 OF 4",
    title: "Let's Start With You",
    lines: [
      "First, a little about you and where things stand right now. Seven short questions, nothing tricky.",
      "We ask so that everything after this fits your real life and not an imagined one. Where a question asks how you are feeling or what else is going on for you, you can always choose not to say."
    ]
  },
  2: {
    label: "SECTION 2 OF 4",
    title: "Mindset and Motivation",
    lines: [
      "This section is about how you like to work: what drives you, how you sit with uncertainty, and what matters most to you.",
      "No answer here is better than another, and nobody is just one thing. Go with what is true of you most of the time, not what sounds impressive. A few questions invite you to answer in your own words. You can type, speak, or be guided through it step by step, and you can say as much or as little as you like."
    ]
  },
  3: {
    label: "SECTION 3 OF 4",
    title: "Financial Situation",
    lines: [
      "Money is the part most people would rather skip, so here is why we ask.",
      "Your numbers are never scored and they never decide your pathway. They help us pace things realistically and be honest with you about timing. Add your income and your costs item by item, weekly, fortnightly, monthly or yearly, whichever way you think about them, and we will do the maths. Rough figures are fine."
    ]
  },
  4: {
    label: "SECTION 4 OF 4",
    title: "Skills Inventory",
    lines: [
      "Last section. This is a plain look at what you can do and where you have done it.",
      "Nothing here is scored and nothing counts against you. You do not need a qualification, a title or a referee. Be specific where you can: a real role, a real result, something someone actually said about your work."
    ]
  },
};

function SectionIntroScreen({ sectionId, onContinue }) {
  const intro = SECTION_INTROS[sectionId];
  if (!intro) return null;
  return (
    <div className="assess-welcome" data-testid={`section-intro-${sectionId}`}>
      <div className="assess-welcome-inner">
        <span className="spring-label">{intro.label}</span>
        <h1 className="assess-welcome-title">{intro.title}</h1>
        <div className="assess-welcome-body">
          {intro.lines.map((line, i) => <p key={i}>{line}</p>)}
        </div>
        <div className="assess-email-capture">
          <button className="assess-btn-primary" onClick={onContinue} data-testid={`section-intro-continue-${sectionId}`}>
            Continue <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================================================================
   SCREEN 3: BEFORE YOU START (Part 7 locked copy)
   =================================================================== */
function BeforeYouStartScreen({ onContinue, attemptId }) {
  return (
    <div className="assess-welcome" data-testid="before-you-start">
      <div className="assess-welcome-inner">
        <span className="spring-label">BEFORE YOU START</span>
        <h1 className="assess-welcome-title">three things worth knowing.</h1>
        <div className="assess-welcome-body">
          <p>This is not a test. There is nothing to pass and no right answers. Be blunt with us: the more honest you are about where you actually are, not where you think you should be, the more useful your results will be.</p>
          <p>You get one full attempt, and it takes around 20 to 30 minutes. You can go back and change any answer until you finish. Nothing is locked in until then.</p>
          <p>If any of this brings something heavy to the surface, that is normal and you are not the only one. You can stop at any point and come back later. Nothing is lost.</p>
        </div>

        <p className="ifb-heading">Quick one before you begin.</p>
        <InlineFeedback attemptId={attemptId} questionIds={WELCOME_FB} />

        <div className="assess-email-capture" style={{ marginTop: 24 }}>
          <button className="assess-btn-primary" onClick={onContinue} data-testid="before-you-start-continue-btn">
            Begin <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================================================================
   SECTION CHECKPOINT
   =================================================================== */
function SectionCheckpoint({ section, onSubmit, onSkip, attemptId }) {
  const [feeling, setFeeling] = useState('');
  const [note, setNote] = useState('');
  const options = ['Easy enough', 'It made me think', 'Hard going', 'Uncomfortable'];
  const sectionFbIds = getSectionFBIds(section);

  return (
    <div className="assess-welcome" data-testid="section-checkpoint">
      <div className="assess-welcome-inner" style={{ maxWidth: 560 }}>
        <p className="assess-checkpoint-label">Quick one before you carry on.</p>
        <h2 className="assess-welcome-title" style={{ fontSize: 24 }}>How was that section?</h2>
        <div className="assess-options compact" style={{ marginBottom: 20 }}>
          {options.map((opt, i) => (
            <button key={i} className={`assess-option ${feeling === opt ? 'selected' : ''}`}
              onClick={() => setFeeling(opt)} data-testid={`checkpoint-${i}`}>{opt}</button>
          ))}
        </div>
        <div className="assess-field-group" style={{ marginBottom: 20 }}>
          <label>Anything you would change about it?</label>
          <input type="text" value={note} onChange={e => setNote(e.target.value)}
            placeholder="Optional" data-testid="checkpoint-note" />
        </div>

        {sectionFbIds.length > 0 && (
          <InlineFeedback attemptId={attemptId} questionIds={sectionFbIds} sectionNum={section} />
        )}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
          <button className="assess-btn-primary" onClick={() => onSubmit(feeling, note)}
            disabled={!feeling} data-testid="checkpoint-submit-btn">Continue</button>
          <button className="assess-btn-secondary" onClick={onSkip} data-testid="checkpoint-skip-btn">
            Skip this
          </button>
        </div>
      </div>
    </div>
  );
}

/* ===================================================================
   RESULTS AND PATHWAY FEEDBACK (Part 5)
   Shown after the pathway report.
   =================================================================== */
function ResultsPathwayFeedback({ attemptId, resultType, onContinue, onSkip }) {
  return (
    <div className="assess-welcome" data-testid="results-pathway-feedback">
      <div className="assess-welcome-inner" style={{ maxWidth: 600 }}>
        <InlineFeedback attemptId={attemptId} questionIds={RESULTS_FB} resultType={resultType} />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 24 }}>
          <button className="assess-btn-primary" onClick={onContinue} data-testid="results-fb-continue-btn">Submit</button>
          <button className="assess-btn-secondary" onClick={onSkip} data-testid="results-fb-skip-btn">Skip this</button>
        </div>
      </div>
    </div>
  );
}

/* ===================================================================
   FINAL OVERALL FEEDBACK (Part 5)
   Shown after results feedback.
   =================================================================== */
function FinalOverallFeedback({ attemptId, onContinue, onSkip }) {
  return (
    <div className="assess-welcome" data-testid="final-overall-feedback">
      <div className="assess-welcome-inner" style={{ maxWidth: 600 }}>
        <InlineFeedback attemptId={attemptId} questionIds={OVERALL_FB} />
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 24 }}>
          <button className="assess-btn-primary" onClick={onContinue} data-testid="overall-fb-continue-btn">Submit</button>
          <button className="assess-btn-secondary" onClick={onSkip} data-testid="overall-fb-skip-btn">Skip this</button>
        </div>
      </div>
    </div>
  );
}


/* ===================================================================
   QUESTION RENDERERS (unchanged from original)
   =================================================================== */
/* Old renderers removed — now in QuestionRenderers.jsx */

/* ===================================================================
   FINANCIAL CALCULATOR
   =================================================================== */
function FinancialCalculator({ value, onChange }) {
  const v = value || { income_items: [{ description: '', amount: '', frequency: 'Monthly' }],
    expense_items: [{ description: '', amount: '', frequency: 'Monthly' }],
    savings_usable: '', one_off_income: '', other_assets: '',
    total_debt_excl_mortgage: '' };
  const updateItem = (listKey, idx, field, val) => {
    const items = [...(v[listKey] || [])]; items[idx] = { ...items[idx], [field]: val }; onChange({ ...v, [listKey]: items });
  };
  const addItem = (listKey) => onChange({ ...v, [listKey]: [...(v[listKey] || []), { description: '', amount: '', frequency: 'Monthly' }] });
  const removeItem = (listKey, idx) => {
    const items = (v[listKey] || []).filter((_, i) => i !== idx);
    onChange({ ...v, [listKey]: items.length ? items : [{ description: '', amount: '', frequency: 'Monthly' }] });
  };
  const toMonthly = (amt, freq) => {
    const n = parseFloat(amt) || 0;
    if (freq === 'Weekly') return n * 4.33; if (freq === 'Fortnightly') return n * 2.166; if (freq === 'Yearly') return n / 12; return n;
  };
  const monthlyIncome = (v.income_items || []).reduce((s, i) => s + toMonthly(i.amount, i.frequency), 0);
  const monthlyExpense = (v.expense_items || []).reduce((s, e) => s + toMonthly(e.amount, e.frequency), 0);
  const savingsUsable = parseFloat(v.savings_usable) || 0;
  const oneOffIncome = parseFloat(v.one_off_income) || 0;
  const runway = monthlyExpense > 0 ? ((savingsUsable + oneOffIncome) / monthlyExpense).toFixed(1) : '0.0';
  const FREQS = ['Weekly', 'Fortnightly', 'Monthly', 'Yearly'];

  // D3 validation: at least one expense with description+amount>0, savings explicitly entered
  const hasRealExpense = (v.expense_items || []).some(e => (e.description || '').trim() && parseFloat(e.amount) > 0);
  const savingsEntered = v.savings_usable !== '' && v.savings_usable !== undefined && v.savings_usable !== null;
  const isValid = hasRealExpense && savingsEntered;

  const renderItemList = (listKey, label) => (
    <div className="fin-list-section">
      <h4>{label}</h4>
      {(v[listKey] || []).map((item, idx) => (
        <div key={idx} className="fin-item-row">
          <input type="text" placeholder="Description" value={item.description || ''} onChange={e => updateItem(listKey, idx, 'description', e.target.value)} className="fin-desc" />
          <input type="number" placeholder="$" value={item.amount || ''} onChange={e => updateItem(listKey, idx, 'amount', e.target.value)} className="fin-amt" />
          <select value={item.frequency || 'Monthly'} onChange={e => updateItem(listKey, idx, 'frequency', e.target.value)} className="fin-freq">
            {FREQS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <button className="fin-remove" onClick={() => removeItem(listKey, idx)}><Trash2 size={16} /></button>
        </div>
      ))}
      <button className="fin-add" onClick={() => addItem(listKey)}><Plus size={14} /> Add item</button>
    </div>
  );
  return (
    <div className="fin-calc" data-testid="financial-calculator">
      {renderItemList('income_items', 'Income')}
      {renderItemList('expense_items', 'Expenses')}
      <div className="fin-singles">
        <h4>Money you could draw on</h4>
        <div className="assess-field-group">
          <label>Savings you could use</label>
          <p className="assess-helper">Money in the bank you could put towards this if you needed to.</p>
          <input type="number" placeholder="$" value={v.savings_usable || ''} onChange={e => onChange({ ...v, savings_usable: e.target.value })} data-testid="fin-savings-usable" />
        </div>
        <div className="assess-field-group">
          <label>One-off money coming in</label>
          <p className="assess-helper">For example a redundancy payment or a lump sum you know is on its way.</p>
          <input type="number" placeholder="$" value={v.one_off_income || ''} onChange={e => onChange({ ...v, one_off_income: e.target.value })} data-testid="fin-one-off" />
        </div>
        <div className="assess-field-group">
          <label>Other assets you could sell or borrow against</label>
          <p className="assess-helper">For example a vehicle, shares or equity in a property. This is shown for context and is not counted in your runway.</p>
          <input type="number" placeholder="$" value={v.other_assets || ''} onChange={e => onChange({ ...v, other_assets: e.target.value })} data-testid="fin-other-assets" />
        </div>
        <div className="assess-field-group">
          <label>Total debt (excluding mortgage)</label>
          <input type="number" placeholder="$" value={v.total_debt_excl_mortgage || ''} onChange={e => onChange({ ...v, total_debt_excl_mortgage: e.target.value })} data-testid="fin-debt" />
        </div>
      </div>
      <div className="fin-summary" data-testid="fin-summary">
        <div className="fin-summary-row"><span>What comes in each month</span><span>${monthlyIncome.toFixed(2)}</span></div>
        <div className="fin-summary-row"><span>What goes out each month</span><span>${monthlyExpense.toFixed(2)}</span></div>
        <div className="fin-summary-row highlight"><span>How long your money would last if your income stopped</span><span>{runway} months</span></div>
      </div>
      <p className="fin-explainer">This is sometimes called your runway. It is simply how many months your savings and any money on its way would cover what you spend each month, if nothing else came in. It is not a judgement, it is a starting point.</p>
      {!isValid && (
        <p className="fin-validation-msg" data-testid="fin-validation-msg">Add at least one monthly expense and enter your savings (even if that is zero) so we can work out your numbers.</p>
      )}
    </div>
  );
}

/* ===================================================================
   MAIN ASSESSMENT FLOW
   =================================================================== */
export default function AssessmentFlow() {
  const navigate = useNavigate();
  // Phases: welcome → (details if pilot code) → before-start → section-intro → questions → checkpoint → completing
  const [phase, setPhase] = useState('welcome');
  const [attemptId, setAttemptId] = useState(null);
  const [participantId, setParticipantId] = useState(null);
  const [pilotCode, setPilotCode] = useState('');
  const [pilotWave, setPilotWave] = useState(1);
  const [email, setEmail] = useState('');
  const [answers, setAnswers] = useState({});
  const [financialData, setFinancialData] = useState(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkpointSection, setCheckpointSection] = useState(null);
  const [introSection, setIntroSection] = useState(null);
  const [showEncouragement, setShowEncouragement] = useState(false);
  const sectionStartRef = useRef(Date.now());

  // Build visible question list based on Appendix A conditional logic
  const q1aRaw = answers['Q1a'] || '';
  const country = typeof q1aRaw === 'object' ? (q1aRaw.selected || '') : q1aRaw;
  const familyWord = country === 'New Zealand' ? 'wh\u0101nau' : 'family';
  const retirementWord = country === 'New Zealand' ? 'leave out KiwiSaver' :
    country === 'Australia' ? 'leave out your super' :
    country === 'United Kingdom' ? 'leave out your pension' : 'leave out retirement savings';

  const allQuestions = QUESTIONS.filter(q => {
    if (!q.conditional) return true;

    // Q2a: shown when Q2 is answers 2, 6 or 7 (Employed but exploring, Between jobs, Unemployed)
    if (q.id === 'Q2a') {
      const q2 = answers['Q2'] || '';
      return ['Employed but exploring options', 'Between jobs / actively job searching', 'Unemployed and not sure what to do'].includes(q2);
    }
    // Q22a: shown unless Q22 is "I'd rather not say"
    if (q.id === 'Q22a') {
      return answers['Q22'] !== "I'd rather not say";
    }
    // Q23a: shown after any "Yes" answer in Q23
    if (q.id === 'Q23a') {
      const q23 = answers['Q23'] || '';
      return q23.startsWith('Yes');
    }
    // Q27: not shown if Q26 includes "Nowhere right now" or "I'd rather not say"
    if (q.id === 'Q27') {
      const q26 = answers['Q26'] || [];
      const q26arr = Array.isArray(q26) ? q26 : [q26];
      return !q26arr.includes('Nowhere right now') && !q26arr.includes("I'd rather not say");
    }
    return false;
  });

  const questionFlow = allQuestions;

  const currentQ = questionFlow[currentIdx];
  const currentSection = currentQ ? ASSESSMENT_SECTIONS.find(s => s.id === currentQ.section) : null;
  const sectionIdx = currentSection ? ASSESSMENT_SECTIONS.findIndex(s => s.id === currentSection.id) + 1 : 0;

  // Each new question opens at the top so the section header is always in view
  useEffect(() => { window.scrollTo(0, 0); }, [currentIdx, phase]);


  // ---- Detect device class ----
  const getDeviceClass = () => {
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 1024) return 'tablet';
    return 'desktop';
  };

  // ---- Handlers ----
  // Email-only start (no pilot code)
  const handleStartEmail = useCallback(async (emailVal) => {
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${API}/api/free-assessment/start`, { email: emailVal });
      setEmail(emailVal);
      setAttemptId(res.data.attempt_id);
      flushPendingFeedback(res.data.attempt_id);
      if (res.data.resumed) {
        const prog = await axios.get(`${API}/api/free-assessment/${res.data.attempt_id}/progress`);
        setAnswers(prog.data.answers || {});
        if (prog.data.financial_profile?.income_items) setFinancialData(prog.data.financial_profile);
        toast.success('Welcome back! We picked up where you left off.');
        sectionStartRef.current = Date.now();
        setIntroSection(null);
        setPhase('questions');
      } else {
        // Pre-fill Q1 email from welcome screen
        setAnswers(prev => ({ ...prev, Q1: { ...(prev.Q1 || {}), email: emailVal } }));
        setPhase('before-start');
      }
    } catch (err) {
      setError(formatErrorDetail(err.response?.data?.detail) || 'Could not start assessment.');
    } finally { setLoading(false); }
  }, []);

  // Start with pilot code + email
  const handleStartCode = useCallback(async (emailVal, code) => {
    setLoading(true); setError(null);
    try {
      // Validate code
      const codeRes = await axios.post(`${API}/api/pilot/validate-code`, { code });
      setPilotCode(code);
      setPilotWave(codeRes.data.wave);
      setEmail(emailVal);
      setPhase('details');
    } catch (err) {
      setError(formatErrorDetail(err.response?.data?.detail) || 'Invalid code.');
    } finally { setLoading(false); }
  }, []);

  const handleDetailsSubmit = useCallback(async (form) => {
    setLoading(true); setError(null);
    try {
      const res = await axios.post(`${API}/api/pilot/register`, form);
      setParticipantId(res.data.participant_id);
      if (res.data.resumed && res.data.attempt_count > 0) {
        toast.success('Welcome back!');
      }
      setPhase('before-start');
    } catch (err) {
      setError(formatErrorDetail(err.response?.data?.detail) || 'Could not register.');
    } finally { setLoading(false); }
  }, []);

  const handleStartAssessment = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let startedId = attemptId;
      if (participantId) {
        // Pilot path — start via pilot endpoint
        const res = await axios.post(`${API}/api/pilot/start-assessment`, {
          participant_id: participantId,
          device_class: getDeviceClass(),
        });
        startedId = res.data.attempt_id;
        setAttemptId(res.data.attempt_id);
      flushPendingFeedback(res.data.attempt_id);
        if (res.data.resumed) {
          const prog = await axios.get(`${API}/api/free-assessment/${res.data.attempt_id}/progress`);
          setAnswers(prog.data.answers || {});
          if (prog.data.financial_profile?.income_items) setFinancialData(prog.data.financial_profile);
          toast.success('Welcome back! We picked up where you left off.');
          sectionStartRef.current = Date.now();
          setPhase('questions');
          return;
        }
      }
      // Show section 1 intro before questions
      sectionStartRef.current = Date.now();
      markSectionStart(startedId, 1);
      setIntroSection(1);
      setPhase('section-intro');
    } catch (err) {
      setError(formatErrorDetail(err.response?.data?.detail) || 'Could not start assessment.');
      toast.error(formatErrorDetail(err.response?.data?.detail) || 'Could not start assessment.');
    } finally { setLoading(false); }
  }, [participantId, attemptId]);

  const saveAnswer = useCallback(async (questionId, value, nextQId) => {
    if (!attemptId) return;
    try {
      await axios.post(`${API}/api/free-assessment/${attemptId}/save-answer`, {
        question_id: questionId, value, next_question_id: nextQId,
      });
      // Save timing
      axios.post(`${API}/api/pilot/timing/${attemptId}`, {
        question_id: questionId, event: 'answer',
        last_question_reached: questionId,
      }).catch(() => {});
    } catch (err) { console.error('Auto-save failed:', err); }
  }, [attemptId]);

  const handleAnswer = (value) => setAnswers(prev => ({ ...prev, [currentQ.id]: value }));

  // Ensure question types with implicit defaults have their values in state
  useEffect(() => {
    if (!currentQ || currentQ.id === '__FINANCIAL__') return;
    const qId = currentQ.id;
    if (answers[qId] !== undefined) return;
    // Initialize ranking with default order
    if (currentQ.type === 'ranking' && currentQ.categories) {
      setAnswers(prev => ({ ...prev, [qId]: { ranking: [...currentQ.categories], example: '' } }));
    }
    // Initialize two_yesno with undefined parts (so user must answer)
    // No default needed — user must select
  }, [currentQ]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNext = async () => {
    const qId = currentQ.id;
    if (qId === '__FINANCIAL__') {
      if (attemptId && financialData) {
        try { await axios.post(`${API}/api/free-assessment/${attemptId}/save-financial`, financialData); } catch {}
      }
    } else {
      const nextQ = questionFlow[currentIdx + 1];
      await saveAnswer(qId, answers[qId], nextQ?.id || qId);
    }

    // Q11 encouragement moment: show after Q11 before moving on
    if (qId === 'Q11') {
      setShowEncouragement(true);
      return;
    }

    advanceToNext();
  };

  // The server keeps the first start time of each section, so a resume never resets it
  const markSectionStart = (id, section) => {
    if (id && section) axios.post(`${API}/api/pilot/timing/${id}`, { event: 'section_start', section }).catch(() => {});
  };

  const advanceToNext = () => {
    // Check if we're transitioning to a new section → show checkpoint then intro
    const nextIdx = currentIdx + 1;
    if (nextIdx < questionFlow.length) {
      const nextQ = questionFlow[nextIdx];
      if (currentQ.section !== nextQ.section && currentQ.section >= 1) {
        // Save section timing
        const duration = Math.round((Date.now() - sectionStartRef.current) / 1000);
        if (attemptId) {
          axios.post(`${API}/api/pilot/timing/${attemptId}`, {
            event: 'section_end', section: currentQ.section, duration_seconds: duration,
          }).catch(() => {});
        }
        // Show checkpoint first, then section intro
        setCheckpointSection(currentQ.section);
        setPhase('checkpoint');
        return;
      }
      setCurrentIdx(nextIdx);
    } else {
      // Last question: save the Section 4 time now, then show its checkpoint before completing
      if (currentQ.section === 4 && phase !== 'completing') {
        if (attemptId) {
          axios.post(`${API}/api/pilot/timing/${attemptId}`, {
            event: 'section_end', section: 4, duration_seconds: Math.round((Date.now() - sectionStartRef.current) / 1000),
          }).catch(() => {});
        }
        setCheckpointSection(4);
        setPhase('checkpoint');
        return;
      }
      handleComplete();
    }
  };

  const handleCheckpointDone = async () => {
    sectionStartRef.current = Date.now();
    const nextIdx = currentIdx + 1;
    const nextQ = questionFlow[nextIdx];
    if (nextQ) markSectionStart(attemptId, nextQ.section);
    setCheckpointSection(null);
    // After section 4 checkpoint, complete the assessment
    if (!nextQ || nextIdx >= questionFlow.length) {
      handleComplete();
      return;
    }
    // Show section intro for the next section
    if (nextQ && SECTION_INTROS[nextQ.section]) {
      setIntroSection(nextQ.section);
      setPhase('section-intro');
    } else {
      setCurrentIdx(nextIdx);
      setPhase('questions');
    }
  };

  const handleCheckpointSkip = () => {
    sectionStartRef.current = Date.now();
    const nextIdx = currentIdx + 1;
    const nextQ = questionFlow[nextIdx];
    if (nextQ) markSectionStart(attemptId, nextQ.section);
    setCheckpointSection(null);
    // After section 4 skip, complete the assessment
    if (!nextQ || nextIdx >= questionFlow.length) {
      handleComplete();
      return;
    }
    if (nextQ && SECTION_INTROS[nextQ.section]) {
      setIntroSection(nextQ.section);
      setPhase('section-intro');
    } else {
      setCurrentIdx(nextIdx);
      setPhase('questions');
    }
  };

  const handleSectionIntroContinue = () => {
    const sId = introSection;
    setIntroSection(null);
    // If this was the first section intro (from before-start), don't increment
    // For subsequent section intros (after checkpoint), currentIdx is already at the right position
    if (sId === 1 && currentIdx === 0) {
      // Starting fresh — stay at index 0
      setPhase('questions');
    } else {
      setCurrentIdx(currentIdx + 1);
      setPhase('questions');
    }
  };

  const handleBack = () => { if (currentIdx > 0) setCurrentIdx(currentIdx - 1); };

  const handleComplete = async () => {
    setPhase('completing');
    const fail = () => { toast.error('Something went wrong reading your answers. Please press Complete Assessment again.'); setPhase('questions'); };
    try {
      await axios.post(`${API}/api/free-assessment/${attemptId}/complete`, {}, { timeout: 30000 });
    } catch (err) {
      console.error('Complete assessment start error:', err?.response?.status, err?.message);
    }
    // The report is prepared in the background; check every 3 seconds for up to 5 minutes
    for (let i = 0; i < 100; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${API}/api/free-assessment/${attemptId}/completion-status`);
        if (data.status === 'completed') {
          navigate(`/assessment/results/${attemptId}`);
          return;
        }
        if (data.status === 'draft') { fail(); return; }
      } catch {}
    }
    fail();
  };

  const isAnswered = () => {
    if (!currentQ) return false;
    const qId = currentQ.id;
    if (qId === '__FINANCIAL__') {
      if (!financialData) return false;
      const hasRealExpense = (financialData.expense_items || []).some(e => (e.description || '').trim() && parseFloat(e.amount) > 0);
      const savingsEntered = financialData.savings_usable !== '' && financialData.savings_usable !== undefined && financialData.savings_usable !== null;
      return hasRealExpense && savingsEntered;
    }
    if (currentQ.optional) return true;
    const val = answers[qId];
    if (val === undefined || val === null || val === '') return false;
    if (currentQ.type === 'name') return val?.full_name && val?.preferred_name && val?.email;
    if (currentQ.type === 'multi') return Array.isArray(val) && val.length > 0;
    if (currentQ.type === 'multi_exact') return Array.isArray(val) && val.length === currentQ.exactCount;
    if (currentQ.type === 'two_part') return val?.partA;
    if (currentQ.type === 'structured_depth') return val?.role && val?.duration && val?.scope && val?.what_you_did;
    if (currentQ.type === 'two_yesno') return val?.paid !== undefined && val?.told !== undefined;
    if (currentQ.type === 'energises_drains') return (val?.energisers?.length > 0);
    if (currentQ.type === 'scale_with_text') return val?.scale > 0;
    if (currentQ.type === 'scale_with_options') return val?.scale > 0;
    if (currentQ.type === 'qualifications') return val?.hasQuals !== undefined && val?.hasQuals !== '';
    if (currentQ.type === 'skills_checklist') return (val?.top?.length || 0) >= (currentQ.maxSkills || 2);
    if (currentQ.type === 'financial_calculator') return val !== undefined && val !== '';
    return true;
  };

  const isLastQuestion = currentIdx >= questionFlow.length - 1;

  // ---- Render ----
  if (phase === 'welcome') return <WelcomeScreen onStartEmail={handleStartEmail} onStartCode={handleStartCode} loading={loading} error={error} />;
  if (phase === 'details') return <DetailsConsentScreen code={pilotCode} wave={pilotWave} email={email} onSubmit={handleDetailsSubmit} loading={loading} error={error} />;
  if (phase === 'before-start') return <BeforeYouStartScreen onContinue={handleStartAssessment} attemptId={attemptId} />;
  if (phase === 'section-intro' && introSection) return <SectionIntroScreen sectionId={introSection} onContinue={handleSectionIntroContinue} />;
  if (phase === 'checkpoint') return <SectionEndFeedback sectionNumber={checkpointSection} attemptId={attemptId} checkpoint={`section_${checkpointSection}`} onComplete={handleCheckpointDone} onSkip={handleCheckpointSkip} />;

  // Encouragement moment after Q11 Part B
  if (showEncouragement) {
    return (
      <div className="assess-completing" data-testid="encouragement-moment">
        <div className="assess-completing-inner" style={{ textAlign: 'center' }}>
          <p style={{ fontSize: 22, fontWeight: 600, color: 'var(--forest)', lineHeight: 1.4, marginBottom: 32, maxWidth: 480 }}>
            Thank you for sharing that. You're doing really well, let's keep going.
          </p>
          <button className="assess-btn-primary" onClick={() => { setShowEncouragement(false); advanceToNext(); }} data-testid="encouragement-continue-btn">
            Continue
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'completing') {
    return (
      <div className="assess-completing">
        <div className="assess-completing-inner">
          <div className="assess-spinner" />
          <h2>Reading what you have told us.</h2>
          <p>We are working through your answers properly. This takes a moment.</p>
        </div>
      </div>
    );
  }

  // Questions phase
  // Apply country words to question text
  const applyCountryWords = (text) => {
    if (!text) return text;
    return text
      .replace(/\[family_word\]/g, familyWord)
      .replace(/\[retirement_word\]/g, retirementWord)
      .replace('A partner, family or wh\u0101nau', country === 'New Zealand' ? 'A partner, family or wh\u0101nau' : 'A partner or family');
  };

  const renderQuestion = () => {
    if (!currentQ) return null;
    const val = answers[currentQ.id];
    // Country words are filled in before display, so the saved answer never holds a placeholder
    const localQ = currentQ.options ? { ...currentQ, options: currentQ.options.map(applyCountryWords) } : currentQ;
    if (currentQ.id === '__FINANCIAL__') return <FinancialCalculator value={financialData} onChange={setFinancialData} />;

    switch (currentQ.type) {
      case 'single': return <SingleSelect q={localQ} value={val} onChange={handleAnswer} />;
      case 'dropdown': {
        const dropVal = typeof val === 'object' ? val?.selected : val;
        const textVal = typeof val === 'object' ? val?.text : '';
        const hasTrigger = currentQ.hasTextBox && dropVal === currentQ.hasTextBox.trigger;
        const setDrop = (v) => {
          if (currentQ.hasTextBox && v === currentQ.hasTextBox.trigger) {
            handleAnswer({ selected: v, text: textVal || '' });
          } else {
            handleAnswer(v);
          }
        };
        return (
          <div className="assess-field-group">
            <select value={dropVal || ''} onChange={e => setDrop(e.target.value)} data-testid="dropdown-input"
              style={{ width: '100%', padding: '14px', fontSize: 17, fontFamily: 'var(--font)', border: '1.5px solid var(--line)', borderRadius: 'var(--radius-button)', color: 'var(--ink)', background: 'var(--white)' }}>
              <option value="">Select...</option>
              {currentQ.options.map((opt, i) => <option key={i} value={applyCountryWords(opt)}>{applyCountryWords(opt)}</option>)}
            </select>
            {hasTrigger && (
              <input type="text" value={textVal || ''} placeholder={currentQ.hasTextBox.placeholder}
                onChange={e => handleAnswer({ selected: dropVal, text: e.target.value })}
                style={{ width: '100%', marginTop: 12, padding: '12px 14px', fontSize: 15, fontFamily: 'var(--font)', border: '1.5px solid var(--line)', borderRadius: 'var(--radius-button)' }}
                data-testid="dropdown-text-input" />
            )}
          </div>
        );
      }
      case 'multi': return <MultiSelect q={localQ} value={val} onChange={handleAnswer} />;
      case 'multi_exact': return <MultiSelect q={localQ} value={val} onChange={handleAnswer} exactCount={currentQ.exactCount} />;
      case 'scale': return <ScaleInput q={currentQ} value={val} onChange={handleAnswer} max={currentQ.scaleMax || 5} />;
      case 'scale_with_text': {
        const sv = typeof val === 'object' ? val : { scale: val };
        return (
          <div>
            <ScaleInput q={currentQ} value={sv.scale} onChange={v => handleAnswer({ ...sv, scale: v })} max={currentQ.scaleMax || 5} />
            <div style={{ marginTop: 24 }}>
              <label style={{ fontSize: 15, fontWeight: 600, color: 'var(--forest)', display: 'block', marginBottom: 8 }}>{currentQ.textPrompt}</label>
              <TextInput q={{}} value={sv.text || ''} onChange={v => handleAnswer({ ...sv, text: v })} />
            </div>
          </div>
        );
      }
      case 'scale_with_options': {
        const sv = typeof val === 'object' ? val : { scale: val };
        return (
          <div>
            <ScaleInput q={currentQ} value={sv.scale} onChange={v => handleAnswer({ ...sv, scale: v })} max={currentQ.scaleMax || 10} />
            {currentQ.partB && (
              <div style={{ marginTop: 32 }}>
                <p style={{ fontSize: 17, fontWeight: 600, color: 'var(--forest)', marginBottom: 12 }}>{currentQ.partB.question}</p>
                <SingleSelect q={{ options: currentQ.partB.options }} value={sv.partB} onChange={v => handleAnswer({ ...sv, partB: v })} />
              </div>
            )}
          </div>
        );
      }
      case 'two_part': {
        const tv = typeof val === 'object' ? val : {};
        return (
          <div>
            <SingleSelect q={{ options: currentQ.optionsA }} value={tv.partA} onChange={v => handleAnswer({ ...tv, partA: v })} />
            <div style={{ marginTop: 32 }}>
              <p style={{ fontSize: 17, fontWeight: 600, color: 'var(--forest)', marginBottom: 12 }}>{currentQ.questionB}</p>
              {currentQ.guidedPrompts && (
                <p style={{ fontSize: 14, color: 'var(--stone)', marginBottom: 12 }}>Guided prompts: {currentQ.guidedPrompts.join(' / ')}</p>
              )}
              <TextInput q={{}} value={tv.partB || ''} onChange={v => handleAnswer({ ...tv, partB: v })} />
            </div>
          </div>
        );
      }
      case 'energises_drains': {
        const ev = typeof val === 'object' ? val : { energisers: [], drains: [] };
        const toggleE = opt => {
          const cur = ev.energisers || [];
          handleAnswer({ ...ev, energisers: cur.includes(opt) ? cur.filter(x => x !== opt) : cur.length < (currentQ.maxEnergises || 3) ? [...cur, opt] : cur });
        };
        const toggleD = opt => {
          const cur = ev.drains || [];
          handleAnswer({ ...ev, drains: cur.includes(opt) ? cur.filter(x => x !== opt) : cur.length < (currentQ.maxDrains || 2) ? [...cur, opt] : cur });
        };
        return (
          <div>
            <div style={{ marginBottom: 32 }}>
              <div className="assess-options">{currentQ.energisers.map((opt, i) => (
                <button key={i} className={`assess-option ${(ev.energisers || []).includes(opt) ? 'selected' : ''}`} onClick={() => toggleE(opt)}>{opt}</button>
              ))}</div>
              <p className="assess-hint">{(ev.energisers || []).length} of {currentQ.maxEnergises || 3} selected</p>
            </div>
            <div>
              <p style={{ fontSize: 17, fontWeight: 600, color: 'var(--forest)', marginBottom: 12 }}>{currentQ.questionDrains}</p>
              <div className="assess-options">{currentQ.drains.map((opt, i) => (
                <button key={i} className={`assess-option ${(ev.drains || []).includes(opt) ? 'selected' : ''}`} onClick={() => toggleD(opt)}>{opt}</button>
              ))}</div>
              <p className="assess-hint">{(ev.drains || []).length} of {currentQ.maxDrains || 2} selected</p>
            </div>
          </div>
        );
      }
      case 'structured_depth': {
        const sv = typeof val === 'object' ? val : {};
        const setField = (key, v) => handleAnswer({ ...sv, [key]: v });
        return (
          <div>
            {currentQ.fields.map(f => (
              <div key={f.key} className="assess-field-group" style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 15, fontWeight: 600, color: 'var(--forest)', marginBottom: 6, display: 'block' }}>
                  {f.label}{!f.optional && ' *'}
                </label>
                {f.inputType === 'dropdown' ? (
                  <select value={sv[f.key] || ''} onChange={e => setField(f.key, e.target.value)} data-testid={`field-${f.key}`}
                    style={{ width: '100%', padding: '12px', fontSize: 15, fontFamily: 'var(--font)', border: '1.5px solid var(--line)', borderRadius: 'var(--radius-button)' }}>
                    <option value="">Select...</option>
                    {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <TextInput q={{}} value={sv[f.key] || ''} onChange={v => setField(f.key, v)} />
                )}
              </div>
            ))}
            {currentQ.examples && (
              <div style={{ marginTop: 24, padding: 20, background: 'var(--blush)', borderRadius: 'var(--radius-card)' }}>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--forest)', marginBottom: 12 }}>Worked examples</p>
                {currentQ.examples.map((ex, i) => (
                  <div key={i} style={{ marginBottom: 16, paddingBottom: 12, borderBottom: i < currentQ.examples.length - 1 ? '1px solid var(--line)' : 'none' }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--forest)', marginBottom: 4 }}>{ex.label}</p>
                    <p style={{ fontSize: 13, color: 'var(--stone)', lineHeight: 1.5, margin: 0 }}>
                      Role or context: {ex.role} / How long: {ex.duration} / What you were responsible for: {ex.scope} / What you did: {ex.what_you_did}{ex.result ? ` / What difference did it make? ${ex.result}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
      case 'skills_checklist': {
        const sv = typeof val === 'object' ? val : { top: [], usedWhere: [], helpWith: '', example: '' };
        const toggleTop = skill => {
          const cur = sv.top || [];
          handleAnswer({ ...sv, top: cur.includes(skill) ? cur.filter(s => s !== skill) : cur.length < (currentQ.maxSkills || 2) ? [...cur, skill] : cur });
        };
        const toggleUsed = opt => {
          const cur = sv.usedWhere || [];
          handleAnswer({ ...sv, usedWhere: cur.includes(opt) ? cur.filter(s => s !== opt) : [...cur, opt] });
        };
        return (
          <div>
            <div className="assess-options">{currentQ.skills.map((s, i) => (
              <button key={i} className={`assess-option ${(sv.top || []).includes(s) ? 'selected' : ''}`} onClick={() => toggleTop(s)}>{s}</button>
            ))}</div>
            <p className="assess-hint">{(sv.top || []).length} of {currentQ.maxSkills || 2} selected</p>

            {currentQ.usedWhere && (
              <div style={{ marginTop: 24 }}>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--forest)', marginBottom: 8 }}>{currentQ.usedWhere.question}</p>
                <div className="assess-options">{currentQ.usedWhere.options.map((o, i) => (
                  <button key={i} className={`assess-option ${(sv.usedWhere || []).includes(o) ? 'selected' : ''}`} onClick={() => toggleUsed(o)}>{o}</button>
                ))}</div>
                <p style={{ fontSize: 14, color: 'var(--stone)', marginTop: 8 }}>{currentQ.usedWhere.subtitle}</p>
              </div>
            )}

            {currentQ.helpWith && (
              <div style={{ marginTop: 24 }}>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--forest)', marginBottom: 8 }}>{currentQ.helpWith.question}</p>
                <SingleSelect q={{ options: currentQ.skills }} value={sv.helpWith} onChange={v => handleAnswer({ ...sv, helpWith: v })} />
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--forest)', marginBottom: 8 }}>{currentQ.examplePrompt}</p>
              <TextInput q={{}} value={sv.example || ''} onChange={v => handleAnswer({ ...sv, example: v })} />
            </div>
          </div>
        );
      }
      case 'qualifications': {
        const qv = typeof val === 'object' ? val : { hasQuals: '' };
        return (
          <div>
            <div className="assess-options" style={{ marginBottom: 20 }}>
              {currentQ.options.map((opt, i) => (
                <button key={i} className={`assess-option ${qv.hasQuals === opt ? 'selected' : ''}`}
                  onClick={() => handleAnswer({ ...qv, hasQuals: opt, qualifications: qv.qualifications?.length ? qv.qualifications : [{}] })}>{opt}</button>
              ))}
            </div>
            {qv.hasQuals === 'Yes' && (
              <div>
                {(qv.qualifications || [{}]).map((qual, qi) => (
                  <div key={qi} style={{ padding: 16, border: '1px solid var(--line)', borderRadius: 'var(--radius-button)', marginBottom: 12 }}>
                    {currentQ.repeatableFields.map(f => (
                      <div key={f.key} className="assess-field-group" style={{ marginBottom: 8 }}>
                        <label style={{ fontSize: 14, fontWeight: 600, color: 'var(--stone)' }}>{f.label}</label>
                        <input type="text" value={qual[f.key] || ''} style={{ width: '100%', padding: '10px', fontSize: 15, border: '1.5px solid var(--line)', borderRadius: 'var(--radius-button)', fontFamily: 'var(--font)' }}
                          onChange={e => {
                            const quals = [...(qv.qualifications || [{}])];
                            quals[qi] = { ...quals[qi], [f.key]: e.target.value };
                            handleAnswer({ ...qv, qualifications: quals });
                          }} />
                      </div>
                    ))}
                  </div>
                ))}
                <button className="assess-btn-secondary" style={{ fontSize: 14, minHeight: 40, padding: '0 16px' }}
                  onClick={() => handleAnswer({ ...qv, qualifications: [...(qv.qualifications || [{}]), {}] })}>
                  <Plus size={14} /> Add another
                </button>
              </div>
            )}
          </div>
        );
      }
      case 'two_yesno': {
        const tv = typeof val === 'object' ? val : {};
        return (
          <div>
            <SingleSelect q={{ options: currentQ.optionsA }} value={tv.paid} onChange={v => handleAnswer({ ...tv, paid: v })} />
            <div style={{ marginTop: 32 }}>
              <p style={{ fontSize: 17, fontWeight: 600, color: 'var(--forest)', marginBottom: 12 }}>{currentQ.questionB}</p>
              <SingleSelect q={{ options: currentQ.optionsB }} value={tv.told} onChange={v => handleAnswer({ ...tv, told: v })} />
            </div>
          </div>
        );
      }
      case 'financial_calculator': return (
        <div>
          <SingleSelect q={{ options: currentQ.options }} value={val} onChange={handleAnswer} />
        </div>
      );
      case 'text': return <input className="assess-text-input" type="text" value={val || ''} onChange={e => handleAnswer(e.target.value)} placeholder={currentQ.placeholder} data-testid="text-input" />;
      case 'textarea': return <TextInput q={currentQ} value={val} onChange={handleAnswer} />;
      case 'name': return (
        <div>
          <NameFields q={currentQ} value={val} onChange={handleAnswer} />
          {val?.preferred_name?.trim() && (
            <p className="assess-subtitle" style={{ marginTop: 16 }} data-testid="q1-after-message">
              {currentQ.afterMessage.replace('[preferred_name]', val.preferred_name.trim())}
            </p>
          )}
        </div>
      );
      default: return <p>Unknown question type: {currentQ.type}</p>;
    }
  };

  return (
    <div className="assess-page" data-testid="assessment-questions">
      {/* Section indicator (per spec: "Section 3 of 4") */}
      <div className="assess-section-indicator">
        {ASSESSMENT_SECTIONS.map((s) => (
          <span key={s.id} className={`assess-section-tab ${currentQ && s.id === currentQ.section ? 'active' : ''} ${currentQ && s.id < currentQ.section ? 'done' : ''}`}>
            {s.name}
          </span>
        ))}
      </div>

      {/* Question card */}
      <div className="assess-card">
        {currentSection && (
          <div className="assess-section-label">Section {sectionIdx} of {ASSESSMENT_SECTIONS.length}: {currentSection.name}</div>
        )}
        {currentQ?.preface?.map((item, i) => (typeof item === 'string'
          ? <p key={i} className="assess-subtitle" data-testid={`question-preface-${i}`}>{applyCountryWords(item)}</p>
          : <ul key={i} className="assess-subtitle" style={{ paddingLeft: 20, margin: '0 0 12px' }} data-testid={`question-preface-${i}`}>
              {item.bullets.map(b => <li key={b}>{applyCountryWords(b)}</li>)}
            </ul>))}
        <h2 className="assess-question-text" data-testid="question-text">
          {applyCountryWords(currentQ?.questionA || currentQ?.questionEnergises || currentQ?.question)}
        </h2>
        {currentQ?.subtitle && <p className="assess-subtitle">{applyCountryWords(currentQ.subtitle)}</p>}
        {currentQ?.helperExample && <p className="assess-helper-example" data-testid="helper-example">{currentQ.helperExample}</p>}
        {currentQ?.permissionLine && <p className="assess-permission">{currentQ.permissionLine}</p>}
        <div className="assess-answer-area" key={currentQ?.id}>{renderQuestion()}</div>
        <p className="assess-save-notice">Your answers are saved. You can close this and come back.</p>
      </div>

      {/* Navigation */}
      <div className="assess-nav">
        <button className="assess-btn-back" onClick={handleBack} disabled={currentIdx === 0} data-testid="back-btn">
          <ArrowLeft size={18} /> Back
        </button>
        <span className="assess-counter">{currentIdx + 1} of {questionFlow.length}</span>
        <button className="assess-btn-primary" onClick={handleNext}
          disabled={!isAnswered() && !currentQ?.optional} data-testid="next-btn">
          {isLastQuestion ? 'Complete Assessment' : 'Continue'}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
